import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReports, DEFAULT_FILTERS } from '../../../hooks/useReports';
import type { DashboardStats } from '../../../hooks/useDashboardData';

const WIDGET_FILTERS = { ...DEFAULT_FILTERS, period: '1m' as const };

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
  stats?: DashboardStats;
  loading?: boolean;
}

type InsightEntry = { icon: string; text: string; type: 'positive' | 'negative' | 'warning' | 'neutral' };

const computeDirectInsights = (stats: DashboardStats): InsightEntry[] => {
  const insights: InsightEntry[] = [];

  // 1. Spending vs income
  if (stats.monthlyIncome > 0 && stats.monthlyExpenses > 0) {
    const ratio = stats.monthlyExpenses / stats.monthlyIncome;
    if (ratio > 1) {
      insights.push({
        icon: 'ri-alert-line',
        text: `Despesas superaram receitas em ${fmt(stats.monthlyExpenses - stats.monthlyIncome)} este mês.`,
        type: 'negative',
      });
    } else if (ratio >= 0.8) {
      insights.push({
        icon: 'ri-error-warning-line',
        text: `Você usou ${Math.round(ratio * 100)}% da receita em despesas — atenção ao limite.`,
        type: 'warning',
      });
    } else {
      const saved = stats.monthlyIncome - stats.monthlyExpenses;
      insights.push({
        icon: 'ri-piggy-bank-line',
        text: `Ótimo! Você economizou ${fmt(saved)} (${Math.round((1 - ratio) * 100)}% da receita) este mês.`,
        type: 'positive',
      });
    }
  }

  // 2. Pending bills alert
  if (stats.pendingBillsCount > 0) {
    insights.push({
      icon: 'ri-bill-line',
      text: `${stats.pendingBillsCount} conta${stats.pendingBillsCount > 1 ? 's' : ''} pendente${stats.pendingBillsCount > 1 ? 's' : ''} nos próximos 30 dias — total ${fmt(stats.pendingBillsTotal)}.`,
      type: stats.pendingBillsCount >= 3 ? 'negative' : 'warning',
    });
  }

  // 3. Goals encouragement
  if (stats.activeGoalsCount > 0) {
    insights.push({
      icon: 'ri-focus-3-line',
      text: `Você tem ${stats.activeGoalsCount} meta${stats.activeGoalsCount > 1 ? 's' : ''} ativa${stats.activeGoalsCount > 1 ? 's' : ''} em andamento. Continue contribuindo!`,
      type: 'positive',
    });
  }

  // 4. No transactions yet
  if (stats.monthlyIncome === 0 && stats.monthlyExpenses === 0) {
    insights.push({
      icon: 'ri-add-circle-line',
      text: 'Nenhum lançamento este mês ainda. Comece registrando suas receitas e despesas.',
      type: 'neutral',
    });
  }

  return insights.slice(0, 3);
};

export const InsightsWidget = ({ stats, loading: statsLoading }: Props) => {
  const { data, loading: reportsLoading, isPremium } = useReports(WIDGET_FILTERS);
  const navigate = useNavigate();

  const directInsights = useMemo<InsightEntry[]>(() => {
    if (!stats) return [];
    return computeDirectInsights(stats);
  }, [stats]);

  const loading = statsLoading || (reportsLoading && !stats);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="h-4 bg-gray-100 rounded w-32 mb-3 animate-pulse" />
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-10 bg-gray-50 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  // Merge: direct insights first, then premium reports insights (deduplicated by text)
  const reportInsights = (isPremium && data?.insights?.length) ? data.insights.slice(0, 2) : [];

  const allInsights: InsightEntry[] = [...directInsights];

  // Only add report insights if no overlap with direct
  reportInsights.forEach((ri) => {
    if (!allInsights.some((d) => d.text.slice(0, 30) === ri.text.slice(0, 30))) {
      allInsights.push({ icon: ri.icon, text: ri.text, type: ri.type as InsightEntry['type'] });
    }
  });

  const displayInsights = allInsights.slice(0, 4);
  if (displayInsights.length === 0) return null;

  const typeStyle: Record<string, string> = {
    positive: 'bg-emerald-50 text-emerald-600',
    negative: 'bg-red-50 text-red-500',
    warning: 'bg-amber-50 text-amber-600',
    neutral: 'bg-gray-50 text-gray-500',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-xl">
            <i className="ri-lightbulb-line text-emerald-500 text-[15px]" />
          </div>
          <h3 className="text-[14px] font-700 text-gray-900">Insights do Mês</h3>
        </div>
        <button
          onClick={() => navigate('/relatorios')}
          className="text-[12px] text-emerald-600 font-600 hover:underline cursor-pointer whitespace-nowrap"
        >
          Ver relatório completo →
        </button>
      </div>
      <div className="space-y-2">
        {displayInsights.map((ins, i) => (
          <div
            key={i}
            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-500 ${typeStyle[ins.type]}`}
          >
            <i className={`${ins.icon} text-[14px] mt-0.5 shrink-0`} />
            <span>{ins.text}</span>
          </div>
        ))}
        {!isPremium && (
          <button
            onClick={() => navigate('/perfil')}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-xl bg-amber-50 text-amber-600 text-[12px] font-500 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line text-[13px] shrink-0" />
            <span>Mais insights detalhados disponíveis no Premium</span>
          </button>
        )}
      </div>
    </div>
  );
};
